import { useQuery } from "@tanstack/react-query";
import { fetchMedWandSummary } from "../utils/api";

const summaryKey = (clientId) => ["medwand-summary", clientId || null];

/**
 * MedWand exam history for one client's Integration panel.
 *
 * There is deliberately no sync mutation here, unlike PacSana and CareLink360.
 * Those are polled — we ask their API for data on a schedule, so a "Sync now"
 * button has something to do. MedWand pushes instead: each exam is delivered to
 * our callback the moment it finishes, so there is nothing to pull and nothing
 * to trigger. The panel simply reads what has already arrived.
 *
 * Returns { linked, totalExams, lastExamAt, latestReadings, upcoming, exams },
 * or { linked: false } when the client has never had a MedWand appointment.
 */
export function useMedWandSummary(clientId, { enabled = true } = {}) {
  return useQuery({
    queryKey: summaryKey(clientId),
    queryFn: () => fetchMedWandSummary(clientId),

    // TEMPORARY — the call is switched off, not deleted.
    //
    // /api/medwand/clients/:id/summary is live on api-dev but returns 404 on
    // the production API, so every request errored and the panel rendered
    // "Could not load MedWand data" instead of its tabs. While the panel is
    // showing sample data anyway, there is nothing to fetch.
    //
    // A disabled query stays pending with fetchStatus "idle", so isLoading and
    // isError are both false and `data` is undefined — which the panel reads as
    // "not linked" and answers with the sample exam or the not-linked message.
    //
    // TO RESTORE, once the MedWand routes are deployed to the production API:
    //   enabled: !!clientId && enabled,
    // and drop this comment. Tracked with the rest of the mock-data teardown.
    enabled: false,

    staleTime: 5 * 60 * 1000,
  });
}
